import { useCallback, useMemo, useState } from 'react';
import type { Game } from '../../../services/games';
import type { FilterState, FilterOptions } from '../types';

const EMPTY_FILTERS: FilterState = {
    search: '',
    genre: '',
    mechanic: '',
    volatility: '',
};

export function useGamesFilter(games: Game[]) {
    const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

    const filterOptions = useMemo(() => buildFilterOptions(games), [games]);

    const filteredGames = useMemo(
        () => games.filter((game) => matchesFilters(game, filters)),
        [games, filters]
    );

    const clearFilters = useCallback(() => {
        setFilters(EMPTY_FILTERS);
    }, []);

    return {
        filters,
        setFilters,
        clearFilters,
        filterOptions,
        filteredGames,
    };
}

function getSearchText(game: Game): string {
    const genres = game.parameters.genres || '';
    return [game.name, game.description, genres].filter(Boolean).join(' ').toLowerCase();
}

function matchesFilters(game: Game, filters: FilterState): boolean {
    const query = filters.search.trim().toLowerCase();
    const searchText = getSearchText(game);
    const cardGenres = game.parameters.genres || '';
    const cardMechanic = game.parameters.mechanic || '';
    const cardVolatility =
        game.parameters.volatility != null ? String(game.parameters.volatility) : '';

    const matchesSearch = !query || searchText.includes(query);
    const cardGenreList = cardGenres
        .split(',')
        .map((genre) => genre.trim())
        .filter(Boolean);
    const matchesGenre = !filters.genre || cardGenreList.includes(filters.genre);
    const matchesMechanic = !filters.mechanic || cardMechanic === filters.mechanic;
    const matchesVolatility = !filters.volatility || cardVolatility === filters.volatility;

    return matchesSearch && matchesGenre && matchesMechanic && matchesVolatility;
}

function buildFilterOptions(games: Game[]): FilterOptions {
    const genresSet = new Set<string>();
    const mechanicsSet = new Set<string>();

    for (const game of games) {
        const genres = game.parameters.genres;
        if (genres) {
            genres
                .split(',')
                .map((genre) => genre.trim())
                .filter(Boolean)
                .forEach((genre) => genresSet.add(genre));
        }

        const mechanic = game.parameters.mechanic;
        if (mechanic) {
            mechanicsSet.add(mechanic);
        }
    }

    return {
        genres: [...genresSet].sort(),
        mechanics: [...mechanicsSet].sort(),
    };
}
